import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'

import { Badge, Card, Container, Overline, Skeleton } from '@/design-system/primitives'
import { cn } from '@/lib/cn'

import { useCandidateResult } from '../api'
import { RoundBreakdown } from '../components'
import { formatScore } from '../score'

/**
 * « Mes résultats » — la fiche privée d'un candidat.
 *
 * On y arrive par le lien reçu par e-mail quand l'organisation décide
 * d'envoyer les résultats. Pas de compte : le jeton du lien suffit, et il
 * n'ouvre que cette fiche-là.
 *
 * Avant la publication des scores du concours — même après la proclamation —
 * la page existe mais ne montre aucun chiffre : le serveur ne les envoie pas.
 * Ce qu'elle montre ensuite : SA note tour par tour (voix du public et points
 * du jury séparés), SON cumul sur 300, SON rang. Des autres, seulement ce que
 * la salle sait déjà.
 */
export function MyResultsPage() {
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const candidateId = searchParams.get('candidate_id')

  const query = useCandidateResult(token, candidateId)

  useEffect(() => {
    document.title = t('myResults.overline')
  }, [t])

  if (!token || !candidateId || query.isError) {
    return (
      <Container className="py-32">
        <h1 className="font-display text-display-md text-content-primary">
          {t('myResults.invalidTitle')}
        </h1>
        <p className="mt-4 max-w-lg text-content-secondary">{t('myResults.invalidHelp')}</p>
        <Link to="/resultats" className="mt-8 inline-block text-champagne-300 underline">
          {t('myResults.seePublic')}
        </Link>
      </Container>
    )
  }

  if (query.isPending) {
    return (
      <Container className="py-24">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-8 h-64 w-full max-w-xl" />
      </Container>
    )
  }

  const result = query.data

  return (
    <Container className="py-16 sm:py-24">
      <Overline>{t('myResults.overline')}</Overline>
      <div className="mt-4 flex items-center gap-4">
        {result.photo_url ? (
          <img
            src={result.photo_url}
            alt=""
            className="h-16 w-16 shrink-0 rounded-full object-cover"
          />
        ) : null}
        <div className="min-w-0">
          <h1 className="font-display text-display-md text-content-primary">{result.name}</h1>
          <p className="text-content-secondary">
            {result.category}
            {result.candidate_number !== null ? (
              <span className="ml-2 font-mono text-content-muted">#{result.candidate_number}</span>
            ) : null}
          </p>
        </div>
      </div>

      {!result.published ? (
        <Card className="mt-10 max-w-xl">
          <h2 className="font-display text-heading-md text-content-primary">
            {t('myResults.notPublishedTitle')}
          </h2>
          <p className="mt-3 text-content-secondary">
            {t('myResults.notPublishedHelp', { category: result.category })}
          </p>
        </Card>
      ) : (
        <div className="mt-10 max-w-xl space-y-6">
          <Card className={cn(result.is_winner && 'hairline-champagne bg-transparent')}>
            {result.is_winner ? (
              <Badge tone="champagne">{t('myResults.winnerBadge')}</Badge>
            ) : null}
            <p className="mt-3 font-display text-heading-lg text-content-primary">
              {result.is_winner
                ? t('myResults.winnerTitle', { category: result.category })
                : result.winner_name
                  ? t('myResults.winnerOther', { name: result.winner_name })
                  : null}
            </p>
            {result.went_to_tiebreak ? (
              <p className="mt-2 text-sm text-content-muted">{t('myResults.tiebreakNote')}</p>
            ) : null}

            {result.rank !== null && result.contestants !== null ? (
              <dl className="mt-6 grid grid-cols-2 gap-4">
                <div>
                  <dt className="overline text-content-muted">{t('myResults.rank')}</dt>
                  <dd className="mt-1 font-display text-display-md tabular-nums text-champagne-300">
                    {result.rank}
                    <span className="text-heading-sm text-content-muted">
                      {' '}
                      / {result.contestants}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="overline text-content-muted">{t('myResults.total')}</dt>
                  <dd className="mt-1 font-display text-display-md tabular-nums text-champagne-300">
                    {result.total !== null ? formatScore(result.total, i18n.language) : '—'}
                    <span className="text-heading-sm text-content-muted"> / 300</span>
                  </dd>
                </div>
              </dl>
            ) : null}
            {result.public_score !== null && result.jury_score !== null ? (
              <p className="mt-3 text-sm text-content-secondary">
                {t('results.split', {
                  public: formatScore(result.public_score, i18n.language),
                  jury: formatScore(result.jury_score, i18n.language),
                })}
              </p>
            ) : null}
          </Card>

          {result.rounds.length > 0 ? (
            <Card>
              <p className="overline text-content-muted">{t('myResults.perRound')}</p>
              <ul className="mt-4 space-y-3">
                {result.rounds.map((round) => (
                  <li
                    key={round.round_name}
                    className="border-b border-line-subtle pb-3 last:border-0 last:pb-0"
                  >
                    <RoundBreakdown name={round.round_name} round={round} />
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          <p className="text-sm text-content-muted">{t('myResults.thanks')}</p>
          <p className="text-caption text-content-muted">{t('myResults.privateNote')}</p>
          <Link to="/resultats" className="inline-block text-champagne-300 underline">
            {t('myResults.seePublic')}
          </Link>
        </div>
      )}
    </Container>
  )
}
